// One-tap build presets. The builder hands over each step's options exactly as
// the customer sees them (already filtered for the frame), so a preset can only
// ever pick something that could be picked by hand. This module only ranks them.
import { partWeight } from './weight';
import { tyreWidths } from './tyreFit';

export const PRESETS = [
  { id: 'lightweight', label: 'Lightweight climber' },
  { id: 'aero', label: 'Aero' },
  { id: 'value', label: 'Best value' },
  { id: 'gravel', label: 'Gravel adventure', gravel: true }
];

// The same signal the pedal ordering uses (frame.category), plus the many
// catalogue frames whose category is unset but whose name says "Gravel".
export function isGravelFrame(frame) {
  return !!frame && /gravel/i.test(`${frame.category || ''} ${frame.name || ''}`);
}

export function presetsFor(frame) {
  if (!frame) return [];
  const gravel = isGravelFrame(frame);
  return PRESETS.filter(preset => !preset.gravel || gravel);
}

// Sizes a tyre is made in (its per-width weights), whether or not in stock.
function sizeWidths(item) {
  try {
    const widths = typeof item.weightWidths === 'string' ? JSON.parse(item.weightWidths) : item.weightWidths;
    return Object.keys(widths || {}).map(Number).filter(Number.isFinite);
  } catch (e) { return []; }
}

const GRAVEL_TYRE_MM = 35;

const text = item => `${item.name || ''} ${item.description || ''}`;

// Riding disciplines an item declares through its category or name
// ("ROAD/GRAVEL", "Gravel", "MTB", "TRI"). No tag means it suits any bike.
// Tyres without either are classed by the sizes they are made in.
export function disciplines(item, stepId) {
  const tags = [];
  const source = `${item.category || ''} ${item.name || ''}`;
  if (/\broad\b/i.test(source)) tags.push('road');
  if (/gravel|xplr|ekar|\bgrx\b|emporia|\bG\d{2}\b/i.test(source)) tags.push('gravel');
  if (/\bmtb\b/i.test(source)) tags.push('mtb');
  if (/\btri\b/i.test(source)) tags.push('tri');
  if (stepId === 'tyres' && !tags.length) {
    const sizes = sizeWidths(item);
    if (sizes.length) tags.push(Math.max(...sizes) >= GRAVEL_TYRE_MM ? 'gravel' : 'road');
  }
  return tags;
}

// 0: made for this terrain (or neutral) · 1: the other drop-bar terrain · 2: MTB/TT only.
function terrainTier(item, terrain, stepId) {
  const tags = disciplines(item, stepId);
  if (!tags.length || tags.includes(terrain)) return 0;
  return tags.some(tag => tag === 'road' || tag === 'gravel') ? 1 : 2;
}

const WORD_DEPTHS = { thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80 };

// Rim depth in mm as the catalogue names encode it: "SES 5.6" → 56,
// "WTO 60" → 60, "Forty Five" → 45, "Zipp 858" → 85. Alloy rims and unnamed
// depths return 0; MTB wheel sizes ("29") never reach here for road builds.
export function rimDepth(item) {
  const name = item && item.name || '';
  if (/\b(alloy|alu)\b/i.test(name)) return 0;
  const decimal = name.match(/\b(\d)\.(\d)\b/);
  if (decimal) return Number(decimal[1] + decimal[2]);
  const words = name.toLowerCase().match(/\b(thirty|forty|fifty|sixty|seventy|eighty)(?:[\s-]+(one|two|three|four|five|six|seven|eight|nine))?\b/);
  if (words) {
    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
    return WORD_DEPTHS[words[1]] + Math.max(0, ones.indexOf(words[2] || ''));
  }
  const numbers = (name.match(/\d{2,3}/g) || []).map(n => n.length === 3 ? Number(n.slice(0, 2)) : Number(n));
  const depths = numbers.filter(n => n >= 20 && n <= 95);
  return depths.length ? Math.max(...depths) : 0;
}

// Aero signals in the data: "aero" in the name/description, then integrated
// one-piece cockpits (Deda Alanera is the catalogue's aero cockpit).
export function aeroScore(item, stepId) {
  const source = text(item);
  let score = /\baero\b|alanera/i.test(source) ? 3 : 0;
  if (stepId === 'bars' && !score && !/generic/i.test(source)) {
    score = /integrat|one-piece/i.test(source) ? 2 : /cockpit/i.test(source) ? 1 : 0;
  }
  if (stepId === 'wheels') score = score * 100 + rimDepth(item);
  return score;
}

function priceOf(item, color) {
  const choice = item.colors && item.colors[color];
  if (!choice) return 0;
  return (choice.color && choice.color.price) || choice.price || 0;
}

function gramsOf(stepId, item, color, tyreWidth) {
  const weight = partWeight({ props: { id: stepId }, item, color, tyreWidth });
  return weight ? weight.grams : Infinity;
}

// Default widths when no preset wants the extreme: a modern road 28 mm and a
// mid-size gravel 42 mm. Gravel without a published frame limit stays at 45 mm.
const TARGET_WIDTH = { road: 28, gravel: 42 };
const UNKNOWN_CLEARANCE_MAX = 45;

// Every (item, colour[, width]) the customer could select for this step.
function candidates(stepId, options, ctx) {
  const list = [];
  options.forEach((item, index) => {
    const colors = item.colors && item.colors.length ? item.colors : [null];
    colors.forEach((_, color) => {
      if (stepId !== 'tyres') return list.push({ item, index, color, tyreWidth: null });
      const widths = tyreWidths(item, color).filter(width => {
        const fit = ctx.fit ? ctx.fit(width) : null;
        return !fit || fit.level !== 'error';
      });
      if (widths.length) return widths.forEach(tyreWidth => list.push({ item, index, color, tyreWidth }));
      // No stock-verified width: still selectable, the customer picks the width
      // later. Ranked last if no size it is made in fits the frame.
      const sizes = sizeWidths(item);
      const misfit = !!ctx.fit && sizes.length > 0 && sizes.every(width => ctx.fit(width).level === 'error');
      list.push({ item, index, color, tyreWidth: null, unsized: misfit ? 2 : 1 });
    });
  });
  return list;
}

function clearanceMax(ctx) {
  const fit = ctx.fit ? ctx.fit(TARGET_WIDTH.gravel) : null;
  return fit && fit.clearance && fit.clearance.maxMm || null;
}

// Sort keys, lowest wins. Ties fall back to the catalogue order the customer
// sees (the builder's own brand/frame-aware sorting), then the first colour.
function keysFor(preset, stepId, c, ctx) {
  const { item, color, tyreWidth } = c;
  const grams = gramsOf(stepId, item, color, tyreWidth);
  const price = priceOf(item, color);
  const target = TARGET_WIDTH[ctx.terrain] || TARGET_WIDTH.road;
  const nearTarget = tyreWidth ? Math.abs(tyreWidth - target) : 0;
  if (preset === 'lightweight') return [grams, tyreWidth || 0];
  if (preset === 'value') return [price, nearTarget, grams];
  if (preset === 'aero') return [-aeroScore(item, stepId), nearTarget, grams];
  if (preset === 'gravel') {
    const gravel = disciplines(item, stepId).includes('gravel') ? 0 : 1;
    let width = tyreWidth;
    if (stepId === 'tyres' && !width) {
      // No stock-verified widths: rank by the sizes the tyre is made in.
      const max = clearanceMax(ctx) || UNKNOWN_CLEARANCE_MAX;
      width = Math.max(0, ...sizeWidths(item).filter(w => w <= max));
    } else if (stepId === 'tyres' && !clearanceMax(ctx) && width > UNKNOWN_CLEARANCE_MAX) {
      width = -width;
    }
    return [gravel, -(width || 0), grams];
  }
  return [];
}

function compare(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] < b[i] ? -1 : 1;
  }
  return 0;
}

// Picks the option a preset wants for one step.
// options: the step's list as displayed. ctx: { terrain: 'road' | 'gravel',
// fit(width) → tyreFit result for this frame and groupset, keep: name of the
// cockpit that comes with the frame }.
// Returns { index, color, tyreWidth } (index into options) or null.
export function pickPart(preset, stepId, options = [], ctx = {}) {
  if (!options.length) return null;
  if (ctx.keep) {
    const index = options.findIndex(item => item.name === ctx.keep);
    if (index !== -1) return { index, color: 0, tyreWidth: null };
  }
  // "No cages" / "No pedals" are only a choice when nothing real is offered.
  const real = options.filter(item => !item.noCages && !item.noPedals);
  const pool = candidates(stepId, options, ctx).filter(c => !real.length || real.includes(c.item));
  const terrain = ctx.terrain || 'road';
  const ranked = pool.map(c => ({
    c,
    keys: [terrainTier(c.item, terrain, stepId), c.unsized || 0, ...keysFor(preset, stepId, c, ctx), c.index, c.color, c.tyreWidth || 0]
  })).sort((a, b) => compare(a.keys, b.keys));
  const best = ranked[0].c;
  return { index: best.index, color: best.color, tyreWidth: best.tyreWidth };
}
